import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTheme } from "@mui/material/styles";
import { propsWithSx } from "designSystem/styles/style-functions";
import React, { forwardRef, FunctionComponent, useState } from "react";
import IconButton, { IconButtonProps } from "../button/IconButton";
import Menu, { MenuProps } from "./Menu";

const paperStyles = (theme) => {
	return {
		marginTop: theme.spacing(1),
	};
};

export interface ActionMenuProps extends Omit<IconButtonProps, "onClick"> {
	/** indicates if user is hovering */
	hovering?: boolean;

	/** properties applied to Menu component */
	menuProps?: Partial<MenuProps>;

	/** handler for open menu */
	onOpen?: (event: React.MouseEvent<HTMLElement>) => Promise<void>;

	/** handler for close menu */
	onClose?: (event: React.MouseEvent<HTMLElement>) => void;

	/** only show button when hovering */
	showOnHover?: boolean;

	/** set to true if menu should use the anchor element as its container (rather than be contained by document body) */
	useAnchorAsContainer?: boolean;

	/** use the down arrow icon */
	useDownArrowIcon?: boolean;
}

const MoreHorizMenu: FunctionComponent<ActionMenuProps> = forwardRef((props, ref) => {
	const {
		children,
		hovering,
		showOnHover,
		onOpen,
		onClose,
		menuProps,
		useAnchorAsContainer,
		useDownArrowIcon,
		...otherProps
	} = props;
	const safeMenuProps = menuProps || {};
	const {
		PaperProps,
		...otherMenuProps
	} = safeMenuProps;

	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = !!anchorEl;

	const onClickButton = async (event: React.MouseEvent<HTMLElement>) => {
		const menuAnchor = event.currentTarget;
		await onOpen?.(event);
		setAnchorEl(menuAnchor);
		menuAnchor.setAttribute("aria-expanded", "true");
	};

	const onCloseMenu = (event: React.MouseEvent<HTMLElement>, reason: "backdropClick" | "escapeKeyDown") => {
		anchorEl?.setAttribute("aria-expanded", "false");
		setAnchorEl(null);
		onClose?.(event);
	};

	if (open || hovering || !showOnHover) {
		return (
			<>
				<IconButton
					onClick={onClickButton}
					size="large"
					{...otherProps}
					ref={ref}
				>
					{useDownArrowIcon ? <ArrowDropDownIcon /> : <MoreHorizIcon />}
				</IconButton>
				<Menu
					PaperProps={propsWithSx(PaperProps, paperStyles(theme))}
					align="right"
					anchorEl={anchorEl}
					container={useAnchorAsContainer ? anchorEl : null}
					onClose={onCloseMenu}
					open={open}
					{...otherMenuProps}
				>
					{children}
				</Menu>
			</>
		);
	}

	return null;
});

export default MoreHorizMenu;
